import { RESOURCES } from "@/constants/resources";
import { useActionJobs } from "@/contexts/ActionJobsContext";
import { actionNoun, countLabel, doneLabel } from "@/helpers/ResourceHelpers";
import { ActionJob, isActiveActionJob } from "@/interfaces/ActionJob";
import { useLazyGetActionJobQuery } from "@/services/actionJobs";
import { faListCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DateTime } from "luxon";
import { FC, useState } from "react";
import { Dropdown, ProgressBar, Spinner } from "react-bootstrap";

const RING_RADIUS = 15;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

const ActionJobRow: FC<{ job: ActionJob }> = ({ job }) => {
  const config = RESOURCES[job.resource];
  const [expanded, setExpanded] = useState(false);
  const [loadDetails, { data: details, isFetching, isError }] =
    useLazyGetActionJobQuery();
  const processed = job.succeeded + job.failed;
  const isActive = isActiveActionJob(job);

  if (!config) return null;

  const toggleFailures = () => {
    if (!expanded) loadDetails(job.id);
    setExpanded((old) => !old);
  };

  return (
    <div className="formalys-job">
      <div className="formalys-job-head">
        <span className="formalys-job-title">
          {actionNoun(config)} de {countLabel(config, job.total)}
        </span>
        <span className="formalys-job-time">
          {DateTime.fromISO(job.createdAt).toRelative({ locale: "fr" })}
        </span>
      </div>

      {isActive ? (
        <>
          <ProgressBar
            now={processed}
            max={job.total}
            className="formalys-job-progress"
            aria-label={`${processed} sur ${job.total}`}
          />
          <span className="formalys-job-meta">
            {processed} / {job.total}
          </span>
        </>
      ) : job.status === "failed" ? (
        <>
          <span className="formalys-job-meta text-danger">
            {job.error ?? "L'action n'a pas pu être exécutée."}
          </span>
          {job.succeeded > 0 && (
            <span className="formalys-job-meta">
              {job.succeeded} {doneLabel(config, job.succeeded)}
            </span>
          )}
        </>
      ) : (
        <span className="formalys-job-meta">
          {job.succeeded} {doneLabel(config, job.succeeded)}
          {job.failed > 0 && ` · ${job.failed} en échec`}
        </span>
      )}

      {!isActive && job.failed > 0 && (
        <button
          type="button"
          className="btn btn-link formalys-job-toggle"
          onClick={toggleFailures}
          aria-expanded={expanded}
        >
          {expanded ? "Masquer les échecs" : "Voir les échecs"}
        </button>
      )}

      {expanded &&
        (isFetching ? (
          <Spinner animation="border" size="sm" className="mt-1" />
        ) : isError ? (
          <span className="formalys-job-meta text-danger">
            Détail des échecs indisponible.
          </span>
        ) : (
          <ul className="formalys-job-failures">
            {details?.failures.map((failure, index) => (
              <li key={failure.targetId ?? index}>
                <strong>{failure.label ?? "Ligne"}</strong> — {failure.error}
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
};

/**
 * En-tête : suivi des actions de masse de l'utilisateur. Un anneau entoure
 * l'icône tant qu'une action tourne ; une pastille compte les bilans non vus.
 */
const ActionJobsIndicator: FC = () => {
  const { jobs, markSeen } = useActionJobs();

  if (jobs.length === 0) return null;

  const active = jobs.filter(isActiveActionJob);
  const unseen = jobs.filter(
    (job) => !isActiveActionJob(job) && !job.seenAt,
  ).length;
  const total = active.reduce((sum, job) => sum + job.total, 0);
  const processed = active.reduce(
    (sum, job) => sum + job.succeeded + job.failed,
    0,
  );
  const progress = total > 0 ? processed / total : 0;

  return (
    <Dropdown
      navbar={true}
      className="formalys-jobs-dropdown"
      as="li"
      autoClose="outside"
      onToggle={(isOpen) => isOpen && markSeen()}
    >
      <Dropdown.Toggle
        bsPrefix="toggle"
        as="div"
        role="button"
        tabIndex={0}
        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.currentTarget.click();
          }
        }}
        aria-label={
          active.length > 0
            ? `Tâches : ${active.length} en cours`
            : "Tâches"
        }
        className="px-2 nav-link cursor-pointer position-relative formalys-jobs-toggle"
      >
        {active.length > 0 && (
          <svg className="formalys-jobs-ring" viewBox="0 0 36 36" aria-hidden="true">
            <circle cx="18" cy="18" r={RING_RADIUS} className="formalys-jobs-ring-track" />
            <circle
              cx="18"
              cy="18"
              r={RING_RADIUS}
              className="formalys-jobs-ring-value"
              strokeDasharray={RING_LENGTH}
              strokeDashoffset={RING_LENGTH * (1 - progress)}
            />
          </svg>
        )}
        <FontAwesomeIcon icon={faListCheck} size="lg" />
        {unseen > 0 && (
          <span
            data-testid="action-jobs-unseen"
            className="position-absolute badge rounded-pill bg-primary"
            style={{ top: 0, left: "70%", fontSize: 9 }}
          >
            {unseen}
          </span>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className="dropdown-caret dropdown-menu-card dropdown-menu-end">
        <div className="formalys-jobs-menu bg-white rounded-2 py-2 dark__bg-1000">
          <h6 className="dropdown-header py-1 fw-bold">Tâches</h6>
          {jobs.map((job) => (
            <ActionJobRow key={job.id} job={job} />
          ))}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ActionJobsIndicator;
